import { GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import FileSaver from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { UserService } from "../../../services/UserService";
import ApsModels from "../../models";
import ApsServices from "../../services";
import commonService from "../../services/CommonService";
import toastStore from "../../stores/ToastStore";
import { usePaging } from "../Common/Pagination";
import { IReportFilters } from "./ReportFilter";
import ReportFiltersAppliedDisplay from "./ReportFiltersAppliedDisplay";

function ISOReportDetail(props: {
  filters: IReportFilters;
  showIndividualReport: (item: ApsModels.IIsoReportDetailItemDto) => void;
  hide?: boolean;
}) {
  const [departmentId, setDepartmentId] = useState(0);
  useEffect(() => {
    const disposer = UserService.currentDepartment.subscribe((id) => {
      setDepartmentId(id);
    });

    return () => {
      disposer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (departmentId > 0) {
      getList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  const [paging, setPaging] = usePaging(1, 100);
  // const pageChange = (page: number, pageSize: number) => {
  //   setPaging({ ...paging, page: page, pageSize: pageSize });
  // };

  const [list, setList] = useState<ApsModels.IIsoReportDetailDto>();
  const [gridApi, setGridApi] = useState<GridApi>(null as any);
  const [gridColumnApi, setGridColumnApi] = useState(null as any);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    toggleColSizes();
  };

  const toggleColSizes = () => {
    if (gridApi && gridColumnApi && list) {
      setTimeout(() => {
        gridApi.sizeColumnsToFit();
        let allColumnIds: any[] = [];
        gridColumnApi.getAllColumns()?.forEach((column: any, i: number) => {
          i !== 0 && allColumnIds.push(column.colId);
        });
        gridColumnApi.autoSizeColumns(allColumnIds, false);
      }, 500);
    }
  };

  const NameCellComponent = (rowProps: {
    data: ApsModels.IIsoReportDetailItemDto;
  }) => {
    return (
      <span
        className="txt-primary pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.showIndividualReport(rowProps.data);
        }}
      >
        {rowProps.data.personnelName}
      </span>
    );
  };

  const PersonnelIdCellComponent = (rowProps: {
    data: ApsModels.IIsoReportDetailItemDto;
  }) => {
    return (
      <span
        className="txt-primary pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.showIndividualReport(rowProps.data);
        }}
      >
        {rowProps.data.personnelId}
      </span>
    );
  };

  const hideColumn = (col: ApsModels.IsoReportCategoryEnum) => {
    return (
      props.filters.trainingCategories.length > 0 &&
      props.filters.trainingCategories.indexOf(col) === -1
    );
  };

  const [gridState, setGridState] = useState({
    columnDefs: [
      // {
      //   headerCheckboxSelection: true,
      //   checkboxSelection: (params: any) => {
      //     return true;
      //   },
      //   field: "_selected",
      //   headerName: "",
      //   width: 10,
      //   resizable: false,
      // },
      {
        field: "personnelName",
        headerName: "Personnel Name",
        cellRenderer: "nameCellComponent",
        suppressSizeToFit: true,
        minWidth: 180,
      },
      {
        field: "personnelId",
        headerName: "Personnel ID",
        cellRenderer: "personnelIdCellComponent",
        suppressSizeToFit: true,
      },
      {
        field: "rank",
        headerName: "Rank",
        suppressSizeToFit: true,
      },
      {
        field: "companyTraining",
        headerName: "Company Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return commonService.toNumberWithComma(param.data.companyTraining);
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.CompanyTraining),
        suppressSizeToFit: true,
      },
      {
        field: "facilitiesTraining",
        headerName: "Facilities Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return commonService.toNumberWithComma(param.data.facilitiesTraining);
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.FacilitiesTraining),
        suppressSizeToFit: true,
      },
      {
        field: "newDriverTraining",
        headerName: "New Driver Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return param.data.displayNewDriverTraining
            ? commonService.toNumberWithComma(param.data.newDriverTraining)
            : "N/A";
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.NewDriverTraining),
        suppressSizeToFit: true,
      },
      {
        field: "driverTraining",
        headerName: "Existing Driver Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return param.data.displayExistingDriverTraining
            ? commonService.toNumberWithComma(param.data.driverTraining)
            : "N/A";
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.DriverTraining),
        suppressSizeToFit: true,
      },
      {
        field: "officerTraining",
        headerName: "Officer Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return param.data.displayOfficeTraining
            ? commonService.toNumberWithComma(param.data.officerTraining)
            : "N/A";
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.OfficerTraining),
        suppressSizeToFit: true,
      },
      {
        field: "hazmatTraining",
        headerName: "HAZMAT Training",
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return commonService.toNumberWithComma(param.data.hazmatTraining);
        },
        hide: hideColumn(ApsModels.IsoReportCategoryEnum.HazmatTraining),
        suppressSizeToFit: true,
      },
      {
        field: "total",
        headerName: "TOTAL",
        width: 100,
        suppressSizeToFit: true,
        valueFormatter: (param: {
          data: ApsModels.IIsoReportDetailItemDto;
        }) => {
          return commonService.toNumberWithComma(param.data.total);
        },
      },
    ],
    defaultColDef: {
      flex: 1,
      filter: true,
      sortable: true,
      resizable: true,
    },
    frameworkComponents: {
      nameCellComponent: NameCellComponent,
      personnelIdCellComponent: PersonnelIdCellComponent,
    },
    autoGroupColumnDef: { minWidth: 200 },
    rowData: [] as ApsModels.IIsoReportDetailItemDto[],
    overlayLoadingTemplate:
      '<span class="ag-overlay-loading-center">Loading</span>',
    overlayNoRowsTemplate:
      '<span class="ag-overlay-loading-center">No Records Found</span>',
  });

  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<{
    name: string;
    id: number;
  }>();

  const getList = async () => {
    gridApi && gridApi.showLoadingOverlay();

    setLoading(true);

    await ApsServices.http.isoReport
      .detailReport({
        users: props.filters.personnelIds,
        isoReportCategoryEnums: props.filters.trainingCategories,
        fromDateString: moment(props.filters.fromDate).format("YYYY-MM-DD"),
        toDateString: moment(props.filters.toDate).format("YYYY-MM-DD"),
      })
      .then((data) => {
        setList(data);
        if (data.isoReportDetailItemDtos.length === 0) {
          gridApi && gridApi.showNoRowsOverlay();
        } else {
          gridApi && gridApi.hideOverlay();
        }
        setLoading(false);
      })
      .catch((err) => {
        toastStore.showError("Failed Getting Course Records", err);
        gridApi && gridApi.hideOverlay();
        setLoading(false);
      });
  };

  //const [rowsSelectedCount, setRowSelectedCount] = useState(0);

  useEffect(() => {
    setGridState({
      ...gridState,
      rowData: list?.isoReportDetailItemDtos || [],
    });
    toggleColSizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useEffect(() => {
    if (gridApi) {
      getList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, member]);

  useEffect(() => {
    if (gridApi) {
      getList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging]);

  const getSelectedRows = () => {
    if (!gridApi) {
      return [];
    }

    const filteredRows: any[] = [];
    gridApi.forEachNodeAfterFilter((node) => {
      filteredRows.push(node);
    });
    const isAnyFilterPresent = gridApi.isAnyFilterPresent();

    return gridApi.getSelectedRows().filter((row) => {
      return (
        !isAnyFilterPresent ||
        !!filteredRows.find((r: any) => r.data.id === row.id)
      );
    });
  };

  const [exporting, setExporting] = useState(false);
  const startExport = async () => {
    setExporting(true);
    await ApsServices.http.isoReport
      .extractIsoDetailReport({
        users: props.filters.personnelIds,
        isoReportCategoryEnums: props.filters.trainingCategories,
        fromDateString: moment(props.filters.fromDate).format("YYYY-MM-DD"),
        toDateString: moment(props.filters.toDate).format("YYYY-MM-DD"),
      })
      .then((data) => {
        const file = commonService.b64toBlob(data.fileContents, "text/plain");
        FileSaver.saveAs(file, data.fileDownloadName);
      })
      .catch((error) => {
        toastStore.showError("Failed ISO Detail Report", error);
      })
      .finally(() => {
        setExporting(false);
      });
  };

  return (
    <>
      <div className={`${props.hide ? "display-none" : "card"}`}>
        <div className="card-body flex flex-col p-4">
          <div className={`flex flex-column h-100`}>
            <div className="pb-4 flex flex-wrap" style={{ fontSize: "16px" }}>
              <div className="flex-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary no-wrap"
                  disabled={exporting}
                  onClick={(e) => {
                    startExport();
                  }}
                >
                  {exporting ? "Export..." : "Export to CSV"}
                </button>
              </div>
              <div className="mt-1">
                <ReportFiltersAppliedDisplay
                  filters={props.filters}
                ></ReportFiltersAppliedDisplay>
              </div>
            </div>
            <div className="flex-1">
              <div style={{ width: "100%", height: "100%" }}>
                <div
                  id="myGrid"
                  style={{
                    height: "100%",
                    minHeight: "300px",
                  }}
                  className="ag-theme-alpine flex-1"
                >
                  <AgGridReact
                    columnDefs={gridState.columnDefs}
                    defaultColDef={gridState.defaultColDef}
                    autoGroupColumnDef={gridState.autoGroupColumnDef}
                    enableRangeSelection={true}
                    animateRows={true}
                    onGridReady={onGridReady}
                    rowSelection={"multiple"}
                    rowMultiSelectWithClick={false}
                    suppressRowDeselection={true}
                    suppressRowClickSelection={true}
                    frameworkComponents={gridState.frameworkComponents}
                    rowData={gridState.rowData || []}
                    onRowDoubleClicked={(event) => {
                      //TODO
                    }}
                    overlayLoadingTemplate={gridState.overlayLoadingTemplate}
                    overlayNoRowsTemplate={gridState.overlayNoRowsTemplate}
                    // onRowSelected={() => {
                    //   setRowSelectedCount(getSelectedRows().length);
                    // }}
                    // onRowDataChanged={() => {
                    //   setRowSelectedCount(getSelectedRows().length);
                    // }}
                    // onFilterChanged={() => {
                    //   setRowSelectedCount(getSelectedRows().length);
                    // }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ISOReportDetail;
